import { isExtendable, type ExtendableGates } from './isExtendable';
import { addDays, lockerDayOf, lockerToday, maxDay } from './lockerDay';

// What the «Ampliar custodia» flow needs to know, resolved from the detail in one place: the
// calendar's floor (also its preloaded value) and the deadline in force. `null` means the action is
// not offered at all — no control is handed to the trazabilidad and the «Expira la recogida»
// milestone renders bare.
// The invariant the placement rests on runs BOTH WAYS: the milestone exists for exactly the one
// status the state gate accepts, so there is neither an orphan anchor (a milestone with no control)
// nor — the direction that is easy to miss — an eligible delivery with no anchor. The second half is
// load-bearing on buildDeliveryTimeline keeping that row for a `stored` parcel with no history at
// all: the control hangs off a row, so no row would mean no action, silently.
export interface ExtendCustodyContext {
  // `YYYY-MM-DD`: the calendar's minDate AND the value it opens on.
  floor: string;
  // `YYYY-MM-DD` in the «buzón» timezone, or null when the delivery has no recorded expiration —
  // a real state (fixture BARE, and the fallback for an unknown id), not a theoretical edge case.
  currentExpirationDay: string | null;
}

export interface ExtendCustodyInput extends ExtendableGates {
  status: string;
  estimatedExpirationDate: string | null;
  lockerTimezone: string;
}

// TWO floors, both binding, and the later one wins:
//   · the deadline in force PLUS ONE DAY — the invariant «Ampliar» can only ever extend, which the
//     UI is what enforces: with the expiration written verbatim, an earlier date would literally
//     SHORTEN custody, and the deadline itself would be a no-op;
//   · TODAY in the «buzón»'s timezone — because the floor is also the calendar's preloaded value, so
//     a floor in the past means the agent's default gesture (accept it and confirm) writes an
//     already-expired deadline from an action called «Ampliar».
// The second clamp is not defensive padding: a `stored` delivery whose deadline already passed is a
// real state, since the stored→expired transition is backend-driven and lags (fixture
// `storedExtended` is exactly this). It ought to be `expired`, which the state gate would reject —
// but «ought to be» is not a guard, and here the cost of trusting it is a wrong write.
// With no deadline recorded there is nothing to extend from, so only today applies.
export function extendCustodyContext(input: ExtendCustodyInput): ExtendCustodyContext | null {
  if (!isExtendable(input.status, input)) return null;
  const currentExpirationDay = lockerDayOf(input.estimatedExpirationDate, input.lockerTimezone);
  const today = lockerToday(input.lockerTimezone);
  return {
    floor:
      currentExpirationDay === null ? today : maxDay(addDays(currentExpirationDay, 1), today),
    currentExpirationDay,
  };
}
